/**
 * Compact Order Grid Component
 *
 * Componente para exibir pedidos em modo compacto (apenas números)
 * Usado quando há muitos pedidos para evitar scroll em TVs
 * Grid dinâmico: sempre 2 linhas, colunas aumentam conforme necessário
 * Tamanho dos números diminui proporcionalmente para caber sempre em 2 linhas
 */

import { useMemo } from 'react';
import { cn } from '@lib/utils';
import type { Order } from './order-card';

interface CompactOrderGridProps {
  orders: Order[];
  className?: string;
}

/**
 * Componente que exibe pedidos em grid compacto dinâmico
 * - Sempre mantém 2 linhas
 * - Aumenta colunas conforme quantidade de pedidos
 * - Ajusta tamanho dos números dinamicamente para caber sempre
 * 
 * Exemplos:
 * - 4 pedidos: 4x2 (4 colunas, 2 linhas)
 * - 6 pedidos: 6x2 (6 colunas, 2 linhas)
 * - 15 pedidos: 15x2 (15 colunas, 2 linhas) - arredondado para cima
 */
export function CompactOrderGrid({ orders, className }: CompactOrderGridProps) {
  // Calcular número de colunas baseado na quantidade de pedidos
  // Sempre manter 2 linhas, então colunas = ceil(orders.length / 2)
  const columns = useMemo(() => {
    if (orders.length === 0) return 4; // Default mínimo
    return Math.ceil(orders.length / 2);
  }, [orders.length]);

  // Calcular tamanho da fonte baseado no número de colunas
  // Mais colunas = fonte menor para caber na tela
  const fontSize = useMemo(() => {
    if (columns <= 4) return 'text-2xl'; // 4x2 ou menos
    if (columns <= 6) return 'text-xl'; // 6x2
    if (columns <= 10) return 'text-lg'; // 10x2
    if (columns <= 15) return 'text-base'; // 15x2
    return 'text-sm'; // Mais de 15 colunas
  }, [columns]);

  // Calcular altura dos cards baseado no número de colunas
  // Mais colunas = cards menores
  const cardHeight = useMemo(() => {
    if (columns <= 4) return 'h-[70px]';
    if (columns <= 6) return 'h-[60px]';
    if (columns <= 10) return 'h-[50px]';
    if (columns <= 15) return 'h-[45px]';
    return 'h-[40px]';
  }, [columns]);

  if (orders.length === 0) {
    return null;
  }

  return (
    <div className={cn('w-full', className)}>
      <div 
        className="grid gap-2 p-3 auto-rows-fr"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {orders.map((order) => (
          <div
            key={order.id}
            className={cn(
              'flex items-center justify-center rounded-xl border-0 bg-white shadow-lg transition-all duration-300 hover:shadow-xl',
              cardHeight,
            )}
          >
            <div className="flex flex-col items-center justify-center rounded-xl bg-brand-primary-200 text-center shadow-md w-full h-full">
              <div className={cn('bg-white/20 rounded px-2 py-1 font-semibold text-black whitespace-nowrap', fontSize)}>
                {order.orderNumber}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
